import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import IconButton from '@mui/material/IconButton'
import InputBase from '@mui/material/InputBase'
import Tooltip from '@mui/material/Tooltip'
import SearchIcon from '@mui/icons-material/Search'
import AppleIcon from '@mui/icons-material/Apple'
import ChatBubbleRoundedIcon from '@mui/icons-material/ChatBubbleRounded'
import FacebookIcon from '@mui/icons-material/Facebook'
import GoogleIcon from '@mui/icons-material/Google'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import LanguageIcon from '@mui/icons-material/Language'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import SmartphoneIcon from '@mui/icons-material/Smartphone'
import Title from '../../component/Title'
import ResetFilterButton from '../../component/ResetFilterButton'
import { LIST_FILTER_SEARCH_FIELD_SX, LIST_FILTER_SEARCH_BUTTON_SX, ListFilterBar, ListFilterForm } from '../../component/ListFilterLayout'
import API from '../../component/Axios'
import Pagination from '../../component/Pagination'
import EditIcon from '../../component/EditIcon'
import ListActionGroup from '../../component/ListActionGroup'
import ListTableContainer from '../../component/ListTableContainer'
import ListEmptyState from '../../component/ListEmptyState'
import Snackbars from '../../component/Snackbars'
import Spiner from '../../component/Spiner'
import { buildListSearch as buildListSearchQuery, createInitialListState } from '../../utils/listPageState'
import { renderDateTime } from '../../utils/dateDisplay'
import { getLastLoginDeviceLabel, getSignupPathLabel, getSignupProviderLabel } from '../../utils/userDisplay'

const DEFAULT_SEARCH_SELECT = {
    searchDevice: '',
    searchCountry: '',
    searchProvider: '',
}

const SEARCH_SELECT_PARAMS = {
    searchDevice: {
        param: 'device',
        defaultValue: DEFAULT_SEARCH_SELECT.searchDevice,
    },
    searchCountry: {
        param: 'country',
        defaultValue: DEFAULT_SEARCH_SELECT.searchCountry,
    },
    searchProvider: {
        param: 'provider',
        defaultValue: DEFAULT_SEARCH_SELECT.searchProvider,
    },
}

const getInitialListState = (location) => {
    return createInitialListState({
        location,
        defaultSearchSelect: DEFAULT_SEARCH_SELECT,
        selectParams: SEARCH_SELECT_PARAMS,
    })
}

const buildListSearch = ({ curPage, searchQuery, searchSelect }) => {
    return buildListSearchQuery({
        curPage,
        searchQuery,
        searchSelect,
        selectParams: SEARCH_SELECT_PARAMS,
    })
}

const SIGNUP_PROVIDER_ICON_STYLES = {
    local: { bgcolor: '#eef2f7', color: '#52606d', borderColor: '#d9e2ec' },
    kakao: { bgcolor: '#fee500', color: '#191919', borderColor: '#fee500' },
    naver: { bgcolor: '#03c75a', color: '#ffffff', borderColor: '#03c75a' },
    google: { bgcolor: '#ffffff', color: '#4285f4', borderColor: '#d9e2ec' },
    apple: { bgcolor: '#111111', color: '#ffffff', borderColor: '#111111' },
    facebook: { bgcolor: '#1877f2', color: '#ffffff', borderColor: '#1877f2' },
}

const getSignupProviderIcon = (provider) => {
    if (provider === 'local') return <MailOutlineIcon sx={{ fontSize: 16 }} />
    if (provider === 'kakao') return <ChatBubbleRoundedIcon sx={{ fontSize: 15 }} />
    if (provider === 'naver') return <Box component="span" sx={{ fontSize: 14, fontWeight: 900, lineHeight: 1 }}>N</Box>
    if (provider === 'google') return <GoogleIcon sx={{ fontSize: 16 }} />
    if (provider === 'apple') return <AppleIcon sx={{ fontSize: 17 }} />
    if (provider === 'facebook') return <FacebookIcon sx={{ fontSize: 17 }} />

    return <HelpOutlineIcon sx={{ fontSize: 16 }} />
}

export function SignupProviderIcon({ provider }) {
    const normalizedProvider = String(provider || '').trim().toLowerCase()
    const label = getSignupProviderLabel(normalizedProvider)
    const displayLabel = label === '-' ? '미확인' : label
    const iconStyle = SIGNUP_PROVIDER_ICON_STYLES[normalizedProvider] || {
        bgcolor: '#f5f5f5',
        color: '#8a94a3',
        borderColor: '#e0e0e0',
    }

    return (
        <Tooltip title={displayLabel} arrow>
            <Box
                component="span"
                role="img"
                aria-label={`가입방식: ${displayLabel}`}
                sx={{
                    ...iconStyle,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 28,
                    height: 28,
                    border: '1px solid',
                    borderRadius: '50%',
                    verticalAlign: 'middle',
                    cursor: 'help',
                }}
            >
                {getSignupProviderIcon(normalizedProvider)}
            </Box>
        </Tooltip>
    )
}

export function SignupPathBadge({ type }) {
    const normalizedType = String(type || '').trim().toLowerCase()
    const pathConfig = normalizedType === 'w'
        ? {
            label: 'WEB',
            accessibleLabel: getSignupPathLabel(normalizedType),
            icon: <LanguageIcon sx={{ fontSize: 15 }} />,
            bgcolor: '#eff6ff',
            color: '#2563eb',
            borderColor: '#bfdbfe',
        }
        : normalizedType === 'a'
            ? {
                label: 'APP',
                accessibleLabel: getSignupPathLabel(normalizedType),
                icon: <SmartphoneIcon sx={{ fontSize: 15 }} />,
                bgcolor: '#f5f3ff',
                color: '#7c3aed',
                borderColor: '#ddd6fe',
            }
            : {
                label: '미확인',
                accessibleLabel: '미확인',
                icon: <HelpOutlineIcon sx={{ fontSize: 15 }} />,
                bgcolor: '#f5f5f5',
                color: '#7b8794',
                borderColor: '#e0e0e0',
            }

    return (
        <Box
            component="span"
            aria-label={`가입경로: ${pathConfig.accessibleLabel}`}
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5,
                minWidth: 68,
                height: 28,
                px: 1,
                border: '1px solid',
                borderColor: pathConfig.borderColor,
                borderRadius: 999,
                bgcolor: pathConfig.bgcolor,
                color: pathConfig.color,
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: '0.04em',
                lineHeight: 1,
                verticalAlign: 'middle',
            }}
        >
            {pathConfig.icon}
            {pathConfig.label}
        </Box>
    )
}

export default function AppUsers() {
    const location = useLocation()
    const history = useHistory()
    const initialListStateRef = useRef(getInitialListState(location))
    const initialListState = initialListStateRef.current
    const [listLoading, setListLoading] = useState(true)
    const [countryLoading, setCountryLoading] = useState(true)
    const [totalCount, setTotalCount] = useState(0)
    const [data, setData] = useState([])
    const [listErrorMessage, setListErrorMessage] = useState('')
    const [countryData, setCountryData] = useState([])
    const [curPage, setCurPage] = useState(initialListState.curPage)
    const offset = 10
    const [openSnackbar, setOpenSnackbar] = useState(initialListState.snackbar.openSnackbar)
    const [severitySnackbar, setSeveritySnackbar] = useState(initialListState.snackbar.severitySnackbar)
    const [messageSnackbar, setMessageSnackbar] = useState(initialListState.snackbar.messageSnackbar)
    const [searchText, setSearchText] = useState(initialListState.searchText)
    const [searchQuery, setSearchQuery] = useState(initialListState.searchQuery)
    const [searchSelect, setSearchSelect] = useState(initialListState.searchSelect)
    const loading = listLoading || countryLoading

    const getData = useCallback(async () => {
        setListLoading(true)
        setListErrorMessage('')

        try {
            const { data } = await API.post('/api/v1/admin/appUser/getList', { page: curPage, offset: offset, searchText: searchQuery, searchSelect: searchSelect })

            if (data.code === 200) {
                setTotalCount(data.data.count || 0)
                setData(data.data.rows || [])
                setListErrorMessage('')
            } else if (data.code === 403) {
                history.push('/login')
                return
            } else {
                const errorMessage = data.message || '회원 목록을 불러오지 못했습니다.'
                setTotalCount(0)
                setData([])
                setListErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            }
        } catch (_error) {
            const errorMessage = '회원 목록을 불러오지 못했습니다.'
            setTotalCount(0)
            setData([])
            setListErrorMessage(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
        } finally {
            setListLoading(false)
        }
    }, [curPage, history, searchQuery, searchSelect])

    useEffect(() => {
        getData()
    }, [getData])

    const getCountryData = useCallback(async () => {
        setCountryLoading(true)

        try {
            const { data } = await API.post('/api/v1/admin/appUser/getCountryList', {  })

            if (data.code === 200) {
                setCountryData(data.data)
            } else if (data.code === 403) {
                history.push('/login')
                return
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('국가 목록을 불러오지 못했습니다.')
        } finally {
            setCountryLoading(false)
        }
    }, [history])

    useEffect(() => {
        getCountryData()
    }, [getCountryData])

    useEffect(() => {
        const nextSearch = buildListSearch({ curPage, searchQuery, searchSelect })

        if (location.search !== nextSearch) {
            history.replace({
                pathname: location.pathname,
                search: nextSearch,
            })
        }
    }, [curPage, history, location.pathname, location.search, searchQuery, searchSelect])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const updateSearchSelect = (e) => {
        if (loading) {
            return
        }

        setCurPage(1)
        setSearchSelect({
            ...searchSelect,
            [e.target.name]: e.target.value
        })
    }

    const resetFilters = () => {
        if (loading) {
            return
        }

        setCurPage(1)
        setSearchText('')
        setSearchQuery('')
        setSearchSelect({
            ...DEFAULT_SEARCH_SELECT,
        })
    }

    const searchButtonClick = () => {
        if (loading) {
            return
        }

        const trimmedSearchText = (searchText || '').trim()

        if (curPage !== 1) {
            setCurPage(1)
            if (searchQuery !== trimmedSearchText) {
                setSearchQuery(trimmedSearchText)
            }
            return
        }

        if (searchQuery !== trimmedSearchText) {
            setSearchQuery(trimmedSearchText)
            return
        }

        getData()
    }

    const hasActiveFilters = Boolean(
        searchQuery ||
        searchSelect.searchDevice ||
        searchSelect.searchProvider ||
        searchSelect.searchCountry
    )

    // 목록 상태에 맞는 빈 화면을 한 곳에서 결정한다.
    const renderEmptyState = () => {
        if (listErrorMessage) {
            return (
                <ListEmptyState
                    variant="error"
                    title="회원 목록을 불러오지 못했습니다."
                    description={listErrorMessage}
                    action={(
                        <Button variant="outlined" size="small" onClick={getData} disabled={loading}>
                            다시 조회
                        </Button>
                    )}
                />
            )
        }

        if (hasActiveFilters) {
            return (
                <ListEmptyState
                    title="조건에 맞는 회원이 없습니다."
                    description="유저ID, 이름, 닉네임, 연락처, 이메일 검색어와 기기/가입 방식/국가 필터를 초기화한 뒤 다시 확인해보세요."
                    action={<ResetFilterButton label="필터 초기화" onClick={resetFilters} disabled={loading} />}
                />
            )
        }

        return (
            <ListEmptyState
                title="조회된 회원이 없습니다."
                description="회원이 생성되면 이 목록에 표시됩니다."
            />
        )
    }

    const handleSearchSubmit = (e) => {
        e.preventDefault()
        searchButtonClick()
    }

    return (
        <Grid item xs={12}>
            {loading ? (
                <Spiner/>
            ) : (
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    {/* 타이틀 */}
                    <Title>회원 목록</Title>
                    {/* 검색폼 */}
                    <ListFilterBar>
                        <ListFilterForm onSubmit={handleSearchSubmit} trailing={<ResetFilterButton onClick={resetFilters} disabled={loading} />}>
                            <InputBase
                                sx={LIST_FILTER_SEARCH_FIELD_SX}
                                placeholder="유저ID/이름/닉네임/연락처/이메일"
                                type="search" 
                                name="searchText"
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                disabled={loading}
                            />
                            <IconButton type="submit" sx={LIST_FILTER_SEARCH_BUTTON_SX} aria-label="search" disabled={loading}><SearchIcon /></IconButton>
                            <Divider sx={{ height: 28, m: 0.5, marginRight:2 }} orientation="vertical" />
                            <TextField
                                label="마지막 로그인 기기"
                                select
                                name="searchDevice"
                                value={searchSelect.searchDevice}
                                onChange={updateSearchSelect}
                                size="small"
                                disabled={loading}
                                sx={{ minWidth: 150 }}
                            >
                                <MenuItem value=''>전체</MenuItem>
                                <MenuItem value={'web'}>Web</MenuItem>
                                <MenuItem value={'and'}>Android</MenuItem>
                                <MenuItem value={'ios'}>iOS</MenuItem>
                            </TextField>
                            <TextField
                                label="가입 방식"
                                select
                                name="searchProvider"
                                value={searchSelect.searchProvider}
                                onChange={updateSearchSelect}
                                size="small"
                                disabled={loading}
                                sx={{ minWidth: 130 }}
                            >
                                <MenuItem value=''>전체</MenuItem>
                                <MenuItem value='local'>이메일</MenuItem>
                                <MenuItem value='kakao'>카카오</MenuItem>
                                <MenuItem value='naver'>네이버</MenuItem>
                                <MenuItem value='google'>구글</MenuItem>
                                <MenuItem value='apple'>애플</MenuItem>
                            </TextField>
                            <TextField
                                label="국가"
                                select
                                name="searchCountry"
                                value={searchSelect.searchCountry}
                                onChange={updateSearchSelect}
                                size="small"
                                disabled={loading}
                                sx={{ minWidth: 150 }}
                            >
                                <MenuItem value=''>전체</MenuItem>
                                {countryData.map((item, index) => (
                                    <MenuItem key={item.countryId} value={item.cCode}>{item.nameKr}</MenuItem>
                                ))}
                            </TextField>
                        </ListFilterForm>
                    </ListFilterBar>
                    {/* 테이블 */}
                    <ListTableContainer>
                        <Table size="small" sx={{ minWidth: 800 }}>
                            <caption>Total Count: {totalCount}</caption>
                            <TableHead>
                                <TableRow>
                                    <TableCell>No</TableCell>
                                    <TableCell align="center">가입 경로</TableCell>
                                    <TableCell align="center">가입 방식</TableCell>
                                    <TableCell>마지막 로그인 기기</TableCell>
                                    <TableCell>국가</TableCell>
                                    <TableCell>User ID</TableCell>
                                    <TableCell>이름</TableCell>
                                    <TableCell>닉네임</TableCell>
                                    <TableCell>연락처</TableCell>
                                    <TableCell>이메일</TableCell>
                                    <TableCell>성별</TableCell>
                                    <TableCell>가입일</TableCell>
                                    <TableCell align="right">Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.length > 0 ? (
                                    data.map((row) => (
                                        <TableRow key={row.userId}>
                                            <TableCell>{row.rownum}</TableCell>
                                            <TableCell align="center"><SignupPathBadge type={row.type} /></TableCell>
                                            <TableCell align="center"><SignupProviderIcon provider={row.signupProvider} /></TableCell>
                                            <TableCell>{getLastLoginDeviceLabel(row.deviceType, row.type)}</TableCell>
                                            <TableCell>{row.app_country?.nameKr}</TableCell>
                                            <TableCell>{row.userId}</TableCell>
                                            <TableCell>{row.username}</TableCell>
                                            <TableCell>{row.nickName}</TableCell>
                                            <TableCell>{row.phone}</TableCell>
                                            <TableCell>{row.email}</TableCell>
                                            <TableCell>{row.gender === 'm' ? '남':'여'}</TableCell>
                                            <TableCell>{renderDateTime(row.regTime)}</TableCell>
                                            <TableCell align="right">
                                                <ListActionGroup>
                                                    <EditIcon path={'user'} id={row.userId} search={location.search} curPage={curPage} searchText={searchQuery} searchSelect={searchSelect} disabled={loading}/>
                                                </ListActionGroup>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={13}>
                                            {renderEmptyState()}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </ListTableContainer>
                    {totalCount > 0 && <Pagination curPage={curPage} offset={offset} totalCount={totalCount} setCurPage={setCurPage} />}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            )}
        </Grid>
    )
}
